import { useLayoutEffect, useState, useRef, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { chatdate, date } from '@/utilities/dateUtils';

import { AccessTime } from '@mui/icons-material';

import { PleaseWait } from '@/components';
import { ctxMessages } from '@/contexts/MessagesCtx';
import { useChatLoader } from '@/hooks/useChatLoader';
import { readMessages } from '@/generated/client/messages/messages';

import './Chat.css';
import { addListSeparatorLine } from '../common/ListSeparator';
import { DetailedMessage } from '@/generated/model';

function DisplayMessage({ message }: { message: DetailedMessage }) {
  const { t, i18n } = useTranslation();

  return (
    <div className="chat-list">
      <div className={message.type === 'sent' ? 'chat-sent' : 'chat-received'}>
        <div className="chat-date-time">
          {chatdate(message.sentStatus.timeMicros, i18n.language, t)} <AccessTime />
        </div>
        {message.type === 'sent' ? (
          <div className="chat-row-sent">
            <div className="chat-username">{message.fromUser.name}</div>
            <div className="chat-sent-text">{message.content.body}</div>
          </div>
        ) : (
          <div className="chat-row-received">
            <div className="chat-received-text">{message.content.body}</div>
            <div className="chat-username">{message.fromUser.name}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function Chat() {
  const ctx = useContext(ctxMessages);
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement | null>(null);
  const [lastMessageTime, setLastMessageTime] = useState<number>(Date.now() * 1000);

  const [conversation, hasMore, scroll] = useChatLoader(ctx?.thread?.threadId, lastMessageTime);
  // ablak meret, scrolltop
  const scrollPos = useRef<[number | undefined, number | undefined]>([undefined, undefined]);

  useEffect(() => {
    function cleanup() {
      window.removeEventListener('scroll', scrollListener, {
        capture: true,
      });
      window.removeEventListener('resize', resizeListener);
    }

    // we use the old behaviour
    // https://developer.chrome.com/blog/viewport-resize-behavior/
    function resizeListener() {
      if (container.current) {
        if (scrollPos.current[0] === undefined || scrollPos.current[1] === undefined) return;
        const h = container.current.parentElement?.parentElement?.clientHeight ?? 0;
        const st = scrollPos.current[1] + (scrollPos.current[0] - h);
        if (container.current.parentElement?.parentElement) {
          container.current.parentElement.parentElement.scrollTop = st;
        }
      }
    }

    function scrollListener() {
      if (!container.current) return;
      scrollPos.current = [
        container.current.parentElement?.parentElement?.clientHeight,
        container.current.parentElement?.parentElement?.scrollTop,
      ];
    }
    window.addEventListener('scroll', scrollListener, {
      passive: true,
      capture: true,
    });
    window.addEventListener('resize', resizeListener);

    return cleanup;
  }, []);

  useLayoutEffect(() => {
    if (scroll) {
      if (container.current && container.current.parentElement && container.current.parentElement.parentElement) {
        container.current.parentElement.parentElement.scrollTop =
          container.current.parentElement.parentElement.scrollHeight;
      }
    }
  });

  useEffect(() => {
    const timer = setTimeout(async () => {
      const unread = conversation?.filter((c) => c.readStatus.name === 'unread').map((c) => c.id);
      if (unread && unread.length) {
        await readMessages(unread);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [conversation]);

  async function loadMore() {
    if (hasMore && conversation) {
      setLastMessageTime(conversation[0].sentStatus.timeMicros);
    }
  }

  // ehelyett suspense kellene
  if (!conversation) {
    return <PleaseWait />;
  }

  if (!conversation.length) {
    return <>{t('chat.noMessage')}</>;
  }

  const groupedMessages = conversation.reduce((acc: { [key: string]: DetailedMessage[] }, message) => {
    const dateKey = date(new Date(message.sentStatus.timeMicros / 1000), t);
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(message);
    return acc;
  }, {});

  return (
    <InfiniteScroll
      isReverse={true}
      pageStart={2}
      initialLoad={false}
      loadMore={loadMore}
      hasMore={hasMore}
      useWindow={false}
      loader={
        <div className="chat-loader" key={0}>
          {t('tracking.loading')}
        </div>
      }
    >
      <div ref={container}>
        {Object.keys(groupedMessages).map((date) => (
          <div key={date}>
            {addListSeparatorLine(date)}
            {groupedMessages[date].map((message) => (
              <DisplayMessage key={message.id} message={message} />
            ))}
          </div>
        ))}
      </div>
    </InfiniteScroll>
  );
}
